// AI Decomposition Service following Single Responsibility Principle
import { apiService } from './api';
import { 
  AIDecompositionResponse, 
  AIDecompositionStructure, 
  AIGoal,
  AIStandaloneOpportunity,
  AIRiskFactor
} from '../types/ai';
import { 
  ProjectPlanningCategory, 
  ProjectTypeDefinition 
} from '../types/project-types';
import { getProjectTypeDefinition } from '../config/project-type-definitions';

// Factory Pattern for creating normalized AI structures
class AIDecompositionFactory {
  static createSafeStructure(
    rawResponse?: any, 
    projectType?: ProjectPlanningCategory
  ): AIDecompositionStructure {
    const structure: AIDecompositionStructure = {
      goals: this.extractGoals(rawResponse, projectType),
      standaloneOpportunities: this.extractOpportunities(rawResponse, projectType),
      risks: this.extractRisks(rawResponse, projectType),
      recommendations: this.extractRecommendations(rawResponse, projectType),
      dependencies: rawResponse?.identifiedDependencies || []
    };

    // Type-specific post-processing
    if (projectType) {
      this.enhanceStructureWithTypeSpecificData(structure, projectType);
    }

    return structure;
  }

  private static extractGoals(raw?: any, projectType?: ProjectPlanningCategory): AIGoal[] {
    if (!raw) return [];
    let goals = raw.generatedGoals || raw.goals || [];
    
    // Type-specific goal enhancement
    if (projectType && goals.length > 0) {
      goals = this.enhanceGoalsWithTypeData(goals, projectType);
    }
    
    return goals;
  }

  private static extractOpportunities(raw?: any, projectType?: ProjectPlanningCategory): AIStandaloneOpportunity[] {
    if (!raw) return [];
    let opportunities = raw.standaloneOpportunities || [];
    
    // Type-specific filtering
    if (projectType === ProjectPlanningCategory.LEARNING_GOAL) {
      // Learning goals focus on competency development opportunities
      opportunities = opportunities.filter((opp: any) => 
        opp.name.toLowerCase().includes('skill') ||
        opp.name.toLowerCase().includes('zertifikat') ||
        opp.name.toLowerCase().includes('kompetenz')
      );
    }
    
    return opportunities;
  }

  private static extractRisks(raw?: any, projectType?: ProjectPlanningCategory): AIRiskFactor[] {
    if (!raw) return [];
    let risks = raw.riskFactors || raw.risks || [];
    
    // Type-specific risk enhancement
    if (projectType && risks.length > 0) {
      risks = this.enhanceRisksWithTypeData(risks, projectType);
    }
    
    return risks;
  }

  private static extractRecommendations(raw?: any, projectType?: ProjectPlanningCategory): string[] {
    if (!raw) return [];
    let recommendations = raw.recommendations || [];
    
    // Add type-specific recommendations
    if (projectType) {
      const typeSpecificRecs = this.getTypeSpecificRecommendations(projectType);
      recommendations = [...recommendations, ...typeSpecificRecs];
    }
    
    return recommendations;
  }

  private static enhanceStructureWithTypeSpecificData(
    structure: AIDecompositionStructure, 
    projectType: ProjectPlanningCategory
  ): void {
    try {
      const definition = getProjectTypeDefinition(projectType);
      
      // Add type-specific metadata to structure
      (structure as any).typeMetadata = {
        projectType,
        characteristics: definition.characteristics,
        requiredArtifacts: definition.artifactTypes.filter(a => a.isRequired),
        planningSteps: definition.planningSteps
      };
    } catch (error) {
      console.warn('Failed to enhance structure with type-specific data:', error);
    }
  }

  private static enhanceGoalsWithTypeData(goals: AIGoal[], projectType: ProjectPlanningCategory): AIGoal[] {
    return goals.map(goal => {
      const enhanced: AIGoal = { ...goal };
      
      // Type-specific goal enhancements
      switch (projectType) {
        case ProjectPlanningCategory.LEARNING_GOAL:
          // Add learning-specific metadata
          if (!enhanced.aiReasoning?.includes('Kompetenz')) {
            enhanced.aiReasoning = `${enhanced.aiReasoning || ''} [Lernziel: Fokus auf Kompetenzerwerb]`.trim();
          }
          break;
          
        case ProjectPlanningCategory.PROJECT_GOAL:
          // Add project milestone indicators
          if (goal.name.toLowerCase().includes('milestone') || goal.name.toLowerCase().includes('meilenstein')) {
            enhanced.standalonePotential = false; // Milestones are usually dependent
          }
          break;
          
        case ProjectPlanningCategory.TECHNICAL_GOAL:
          // Add technical complexity indicators
          if (goal.name.toLowerCase().includes('implement') || goal.name.toLowerCase().includes('develop')) {
            enhanced.complexityScore = Math.max(enhanced.complexityScore || 5, 6); // Technical goals tend to be more complex
          }
          break;
          
        case ProjectPlanningCategory.VISION_GOAL:
          // Vision goals are typically high-level with long timeframes
          enhanced.estimatedHours = enhanced.estimatedHours ? enhanced.estimatedHours * 2 : undefined; // Vision goals take longer
          break;
          
        case ProjectPlanningCategory.PERSONAL_GOAL:
          // Personal goals often have high standalone potential
          enhanced.standalonePotential = enhanced.standalonePotential !== false ? true : false;
          break;
      }
      
      return enhanced;
    });
  }

  private static enhanceRisksWithTypeData(risks: AIRiskFactor[], projectType: ProjectPlanningCategory): AIRiskFactor[] {
    const typeSpecificRisks: AIRiskFactor[] = [];
    
    // Add common risks for each type
    switch (projectType) {
      case ProjectPlanningCategory.LEARNING_GOAL:
        typeSpecificRisks.push({
          risk: 'Lernplateau oder Motivationsverlust',
          probability: 0.3,
          impact: 'medium',
          mitigation: 'Regelmäßige Fortschrittsmessung und Belohnungssystem einführen'
        });
        break;
        
      case ProjectPlanningCategory.PROJECT_GOAL:
        typeSpecificRisks.push({
          risk: 'Deadline-Druck und Scope Creep',
          probability: 0.4,
          impact: 'high',
          mitigation: 'Klare Anforderungen definieren und Change Management etablieren'
        });
        break;
        
      case ProjectPlanningCategory.TECHNICAL_GOAL:
        typeSpecificRisks.push({
          risk: 'Technische Schulden und Integration-Probleme',
          probability: 0.35,
          impact: 'medium',
          mitigation: 'Code Reviews, Tests und kontinuierliche Integration verwenden'
        });
        break;
    }
    
    return [...risks, ...typeSpecificRisks];
  }

  private static getTypeSpecificRecommendations(projectType: ProjectPlanningCategory): string[] {
    const recommendations: string[] = [];
    
    switch (projectType) {
      case ProjectPlanningCategory.LEARNING_GOAL:
        recommendations.push(
          'Verwende Spaced Repetition für bessere Wissensverfestigung',
          'Definiere konkrete, messbare Lernergebnisse für jeden Lernabschnitt',
          'Integriere praktische Anwendungen und Projekte in den Lernplan'
        );
        break;
        
      case ProjectPlanningCategory.PROJECT_GOAL:
        recommendations.push(
          'Erstelle einen detaillierten Meilenstein-Plan mit Buffer-Zeiten',
          'Definiere klare Verantwortlichkeiten und Kommunikationswege',
          'Implementiere regelmäßige Stakeholder-Updates und Review-Zyklen'
        );
        break;
        
      case ProjectPlanningCategory.PERSONAL_GOAL:
        recommendations.push(
          'Etabliere täglich kleine, konsistente Gewohnheiten',
          'Tracke Fortschritt visuell (z.B. mit Charts oder Apps)',
          'Baue soziale Rechenschaftspflicht und Support-Systeme auf'
        );
        break;
        
      case ProjectPlanningCategory.VISION_GOAL:
        recommendations.push(
          'Entwickle mehrere Szenarien und alternative Pfade zur Vision',
          'Definiere messbare Zwischenziele und Key Performance Indicators',
          'Plane regelmäßige Strategie-Reviews und Kursanpassungen'
        );
        break;
        
      case ProjectPlanningCategory.TECHNICAL_GOAL:
        recommendations.push(
          'Beginne mit einem Minimal Viable Product (MVP) Ansatz',
          'Implementiere Test-Driven Development von Anfang an',
          'Plane technische Spikes für Risiko-Validierung und Proof of Concepts'
        );
        break;
    }
    
    return recommendations;
  }
}

// Service class following Single Responsibility
export class AIDecompositionService {
  /**
   * Decompose project using AI with type-specific planning
   * @param projectId - UUID of the project
   * @param input - Project description and context with type-specific data
   * @returns Normalized AI decomposition response
   */
  static async decomposeProject(
    projectId: string, 
    input: {
      description: string;
      projectType: ProjectPlanningCategory | string;
      techStack?: string[];
      architecture?: string;
      existingAssets?: string[];
      budget?: number;
      timeline?: string;
      businessModel?: string;
      targetAudience?: string;
      // Type-specific configuration data
      typeSpecificData?: Record<string, any>;
      planningMetadata?: {
        triggerType?: string;
        timeHorizon?: string;
        measurementFocus?: string;
      };
    }
  ): Promise<AIDecompositionResponse> {
    try {
      // Get type-specific configuration
      const enhancedInput = this.enhanceInputWithTypeSpecificData(input);
      
      const response = await apiService.decomposeProject(projectId, enhancedInput);
      
      // Validate response structure
      if (!response?.decomposition) {
        throw new Error('Invalid API response: missing decomposition');
      }

      const rawDecomp = response.decomposition;

      // Create type-specific normalized structure using Factory
      const normalizedStructure = AIDecompositionFactory.createSafeStructure(
        rawDecomp.generatedStructure || rawDecomp,
        input.projectType as ProjectPlanningCategory
      );

      // Type-safe response construction
      const normalizedResponse: AIDecompositionResponse = {
        id: rawDecomp.id || '',
        projectId,
        confidence: rawDecomp.confidenceScore || 0,
        processingTime: 0,
        confidenceScore: rawDecomp.confidenceScore || 0,
        generatedStructure: normalizedStructure,
        wasAccepted: false,
        createdAt: new Date().toISOString()
      };

      return normalizedResponse;

    } catch (error) {
      console.error('AI Decomposition Service Error:', error);
      throw new Error(`AI decomposition failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Enhance input with type-specific data and AI prompt template
   * @private
   */
  private static enhanceInputWithTypeSpecificData(input: any): any {
    // If projectType is not a valid ProjectPlanningCategory, return original input
    if (!Object.values(ProjectPlanningCategory).includes(input.projectType as ProjectPlanningCategory)) {
      return input;
    }

    try {
      const projectType = input.projectType as ProjectPlanningCategory;
      const definition = getProjectTypeDefinition(projectType);
      
      // Create enhanced input with type-specific prompt
      const enhancedInput = {
        ...input,
        // Add type-specific metadata
        planningCategory: projectType,
        planningCharacteristics: definition.characteristics,
        planningSteps: definition.planningSteps,
        expectedArtifacts: definition.artifactTypes,
        // Enhanced AI prompt with type-specific template
        aiPromptTemplate: definition.aiPromptTemplate,
        // Validation rules for quality checks
        validationRules: definition.validationRules,
        // Include type-specific context
        typeSpecificContext: {
          triggerType: definition.characteristics.triggerType,
          timeHorizon: definition.characteristics.timeHorizon,
          measurementFocus: definition.characteristics.measurementFocus,
          requiredFields: definition.requiredFields,
          ...input.typeSpecificData
        }
      };

      return enhancedInput;
    } catch (error) {
      console.warn('Failed to enhance input with type-specific data:', error);
      return input;
    }
  }

  /**
   * Generate type-specific AI goals
   * @param projectId - Project ID
   * @param goalDescription - Description for the goal
   * @param projectType - Type of project for context
   * @param parentGoalId - Optional parent goal ID
   * @returns AI-generated goal structure
   */
  static async generateTypeSpecificGoal(
    projectId: string,
    goalDescription: string,
    projectType: ProjectPlanningCategory,
    parentGoalId?: string,
    additionalContext?: Record<string, any>
  ): Promise<AIGoal[]> {
    try {
      const definition = getProjectTypeDefinition(projectType);
      
      const input = {
        description: goalDescription,
        projectType,
        parentGoalId,
        typeSpecificData: additionalContext,
        planningMetadata: {
          triggerType: definition.characteristics.triggerType,
          timeHorizon: definition.characteristics.timeHorizon,
          measurementFocus: definition.characteristics.measurementFocus
        }
      };

      // Use the enhanced input with type-specific context
      const enhancedInput = this.enhanceInputWithTypeSpecificData(input);
      
      const response = await apiService.generateGoal(projectId, enhancedInput);
      
      if (!response?.generatedStructure?.goals) {
        throw new Error('Invalid API response: missing goals');
      }

      return response.generatedStructure.goals;
    } catch (error) {
      console.error('Type-specific goal generation error:', error);
      throw new Error(`Failed to generate type-specific goal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Validate AI decomposition against type-specific rules
   * @param decomposition - The decomposition to validate
   * @param projectType - Type of project for validation context
   * @returns Validation result with errors and warnings
   */
  static validateDecomposition(
    decomposition: AIDecompositionStructure,
    projectType: ProjectPlanningCategory
  ): { isValid: boolean; errors: string[]; warnings: string[] } {
    try {
      const definition = getProjectTypeDefinition(projectType);
      const errors: string[] = [];
      const warnings: string[] = [];

      // Validate required artifacts based on type
      const requiredArtifacts = definition.artifactTypes.filter(a => a.isRequired);
      requiredArtifacts.forEach(artifact => {
        const hasArtifact = decomposition.goals.some(goal => 
          goal.name.toLowerCase().includes(artifact.name.toLowerCase()) ||
          goal.description?.toLowerCase().includes(artifact.name.toLowerCase())
        );
        
        if (!hasArtifact) {
          warnings.push(`Empfohlenes Artefakt nicht gefunden: ${artifact.name}`);
        }
      });

      // Validate planning steps coverage
      const requiredSteps = definition.planningSteps.filter(s => s.isRequired);
      requiredSteps.forEach(step => {
        const hasStep = decomposition.goals.some(goal => 
          goal.name.toLowerCase().includes(step.name.toLowerCase()) ||
          goal.description?.toLowerCase().includes(step.name.toLowerCase())
        );
        
        if (!hasStep) {
          warnings.push(`Erforderlicher Planungsschritt nicht abgedeckt: ${step.name}`);
        }
      });

      // Type-specific validations
      switch (projectType) {
        case ProjectPlanningCategory.LEARNING_GOAL:
          if (!decomposition.goals.some(g => g.name.toLowerCase().includes('lernergebnis') || g.name.toLowerCase().includes('outcome'))) {
            errors.push('Lernziele müssen konkrete Lernergebnisse definieren');
          }
          break;
          
        case ProjectPlanningCategory.PROJECT_GOAL:
          if (!decomposition.goals.some(g => g.name.toLowerCase().includes('meilenstein') || g.name.toLowerCase().includes('milestone'))) {
            warnings.push('Projektziele sollten klare Meilensteine enthalten');
          }
          break;
          
        case ProjectPlanningCategory.TECHNICAL_GOAL:
          if (!decomposition.goals.some(g => g.name.toLowerCase().includes('test') || g.name.toLowerCase().includes('spec'))) {
            warnings.push('Technische Ziele sollten Tests oder Spezifikationen enthalten');
          }
          break;
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    } catch (error) {
      console.error('Decomposition validation error:', error);
      return {
        isValid: false,
        errors: ['Validierung fehlgeschlagen'],
        warnings: []
      };
    }
  }

  /**
   * Accept AI decomposition and create goals
   * @param decompositionId - ID of the decomposition to accept
   */
  static async acceptDecomposition(decompositionId: string): Promise<void> {
    try {
      await apiService.acceptDecomposition(decompositionId);
    } catch (error) {
      console.error('Accept Decomposition Service Error:', error);
      throw new Error(`Failed to accept decomposition: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Submit feedback for AI decomposition
   * @param decompositionId - ID of the decomposition
   * @param rating - Rating 1-5
   * @param feedback - Optional feedback text
   */
  static async submitFeedback(
    decompositionId: string, 
    rating: number, 
    feedback?: string
  ): Promise<void> {
    if (rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }

    try {
      await apiService.submitDecompositionFeedback(decompositionId, { rating, feedback });
    } catch (error) {
      console.error('Submit Feedback Service Error:', error);
      throw new Error(`Failed to submit feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Export singleton instance
export const aiDecompositionService = AIDecompositionService;